import React, { useState, useMemo } from 'react';
import PlayerStatsChart from '../../charts/PlayerStatsChart';
import {LockClosedIcon} from '@heroicons/react/20/solid';
import Dropdown from '../../Assets/Dropdown';

const ChartContainer = ({ playerGames, sport, position, playerType, isLoading, isError, isSubActive }) => {
    // Define stat options based on sport and position/type
    const statOptions = useMemo(() => {
        if (sport === 'nba') {
            return [
                { value: 'Points', label: 'Points'},
                { value: 'PlusMinus', label: 'Plus/Minus' },
                { value: 'Minutes', label: 'Minutes', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                { value: 'Rebounds', label: 'Rebounds', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                { value: 'Assists', label: 'Assists', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                { value: 'BlockedShots', label: 'Blocked Shots', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                { value: 'DoubleDoubles', label: 'Double Doubles', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                { value: 'ThreePointersMade', label: 'Three Pointers Made', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                { value: 'FieldGoalsPercentage', label: 'Field Goal %', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                { value: 'EffectiveFieldGoalsPercentage', label: 'Effective FG %', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                { value: 'UsageRatePercentage', label: 'Usage Rate %', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
            ];
        } else if (sport === 'nfl') {
            if (position === 'QB') {
                return [
                    { value: 'PassingYards', label: 'Passing Yards' },
                    { value: 'PassingCompletionPercentage', label: 'Completion %' },
                    { value: 'PassingYardsPerAttempt', label: 'Yards Per Attempt', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'PassingYardsPerCompletion', label: 'Yards Per Completion', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'PassingTouchdowns', label: 'Passing TDs', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'PassingInterceptions', label: 'Interceptions', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'PassingRating', label: 'Passer Rating', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'RushingAttempts', label: 'Rushing Attempts', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'RushingYards', label: 'Rushing Yards', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'RushingYardsPerAttempt', label: 'Rush Yards Per Attempt', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                ];
            } else if (['RB', 'FB'].includes(position)) {
                return [
                    { value: 'RushingAttempts', label: 'Rushing Attempts' },
                    { value: 'RushingYards', label: 'Rushing Yards' },
                    { value: 'RushingYardsPerAttempt', label: 'Rush Yards Per Attempt', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'Receptions', label: 'Receptions', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'ReceivingYards', label: 'Receiving Yards', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'ReceivingYardsPerReception', label: 'Yards Per Reception', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'ReceivingTouchdowns', label: 'Receiving TDs', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'TotalYards', label: 'Total Yards', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'TotalTouchdowns', label: 'Total TDs', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                ];
            } else if (['LWR', 'RWR', 'SWR', 'TE'].includes(position)) {
                return [
                    { value: 'Receptions', label: 'Receptions' },
                    { value: 'ReceivingYards', label: 'Receiving Yards' },
                    { value: 'ReceivingYardsPerReception', label: 'Yards Per Reception', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'ReceivingTouchdowns', label: 'Receiving TDs', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'TotalYards', label: 'Total Yards', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'TotalTouchdowns', label: 'Total TDs', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                ];
            }
        } else if (sport === 'mlb') {
            if (playerType === 'pitcher') {
                return [
                    { value: 'EarnedRunAverage', label: 'ERA' },
                    { value: 'PitchingHits', label: 'Hits Allowed' },
                    { value: 'PitchingEarnedRuns', label: 'Earned Runs', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'PitchingStrikeouts', label: 'Strikeouts', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'WalksHitsPerInningsPitched', label: 'WHIP', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'PitchingBattingAverageAgainst', label: 'Opp BA', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'PitchingStrikeoutsPerNineInnings', label: 'K/9', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                ];
            } else {
                return [
                    { value: 'Runs', label: 'Runs' },
                    { value: 'Hits', label: 'Hits' },
                    { value: 'HomeRuns', label: 'Home Runs', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'RunsBattedIn', label: 'RBIs', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'BattingAverage', label: 'Batting Average', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'StolenBases', label: 'Stolen Bases', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'OnBasePlusSlugging', label: 'OPS', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'TotalBases', label: 'Total Bases', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                    { value: 'BallsInPlay', label: 'Balls In Play', icon: !isSubActive ? <LockClosedIcon width={15} /> : null, disabled: !isSubActive },
                ];
            }
        }
        return [];
    }, [sport, position, playerType]);

    const [selectedStat, setSelectedStat] = useState(statOptions[0]?.value || '');

    // Update selected stat if options change
    React.useEffect(() => {
        if (statOptions.length > 0 && !statOptions.find(opt => opt.value === selectedStat)) {
            setSelectedStat(statOptions[0].value);
        }
    }, [statOptions, selectedStat]);

    // Calculate derived stats
    const getStatValue = (game, statKey) => {
        if (statKey === 'TotalYards') {
            const rushing = game.RushingYards || 0;
            const receiving = game.ReceivingYards || 0;
            return rushing + receiving;
        } else if (statKey === 'TotalTouchdowns') {
            const rushing = game.RushingTouchdowns || 0;
            const receiving = game.ReceivingTouchdowns || 0;
            return rushing + receiving;
        }
        return game[statKey];
    };

    // Transform data for chart
    const chartData = useMemo(() => {
        if (!playerGames || playerGames.length === 0) {
            return null;
        }

        // Reverse to show oldest to newest (left to right)
        const reversedGames = [...playerGames].reverse();

        const labels = reversedGames.map(game => {
            // Create label from date or opponent
            if (game.DateTime) {
                const date = new Date(game.DateTime);
                return `${date.getMonth() + 1}/${date.getDate()}`;
            }
            return game.Opponent || 'Game';
        });

        const data = reversedGames.map(game => {
            const value = getStatValue(game, selectedStat);
            return value !== null && value !== undefined ? value : 0;
        });

        return {
            labels,
            datasets: [
                {
                    label: statOptions.find(opt => opt.value === selectedStat)?.label || selectedStat,
                    data,
                    backgroundColor: 'rgba(59, 130, 246, 0.5)', // Blue
                    borderColor: 'rgba(59, 130, 246, 1)',
                    borderWidth: 2,
                },
            ],
        };
    }, [playerGames, selectedStat, statOptions]);

    // Chart options
    const chartOptions = useMemo(() => {
        const selectedLabel = statOptions.find(opt => opt.value === selectedStat)?.label || selectedStat;
        
        return {
            scales: {
                y: {
                    beginAtZero: true,
                    ticks: {
                        color: '#6B7280', // gray-500
                    },
                    grid: {
                        color: 'rgba(107, 114, 128, 0.1)',
                    },
                },
                x: {
                    ticks: {
                        color: '#6B7280', // gray-500
                    },
                    grid: {
                        color: 'rgba(107, 114, 128, 0.1)',
                    },
                },
            },
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    backgroundColor: 'rgba(17, 24, 39, 0.9)', // gray-900
                    titleColor: '#F9FAFB', // gray-50
                    bodyColor: '#F9FAFB',
                    borderColor: 'rgba(75, 85, 99, 0.5)',
                    borderWidth: 1,
                    padding: 12,
                    displayColors: false,
                    callbacks: {
                        label: (context) => {
                            const value = context.parsed.y;
                            // Format percentage stats
                            if (selectedStat.includes('Percentage') || selectedStat === 'BattingAverage') {
                                return `${selectedLabel}: ${value.toFixed(3)}`;
                            }
                            // Format decimal stats
                            if (selectedStat.includes('Average') || selectedStat.includes('PerAttempt') || 
                                selectedStat.includes('PerReception') || selectedStat.includes('PerCompletion') ||
                                selectedStat === 'WalksHitsPerInningsPitched' || selectedStat === 'OnBasePlusSlugging') {
                                return `${selectedLabel}: ${value.toFixed(2)}`;
                            }
                            // Default to whole number
                            return `${selectedLabel}: ${value}`;
                        },
                    },
                },
            },
        };
    }, [selectedStat, statOptions]);

    if (isLoading) {
        return (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-6 mt-6">
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    Loading game data...
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-6 mt-6">
                <div className="text-center py-8 text-red-500 dark:text-red-400">
                    Error loading game data
                </div>
            </div>
        );
    }

    if (!playerGames || playerGames.length === 0) {
        return (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-6 mt-6">
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No game data available
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-6 mt-6">
            <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    Last {playerGames.length} Games
                </h3>
                <div className="flex items-center gap-3">
                    {/* <label htmlFor="stat-select" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Select Stat:
                    </label> */}
                    {/* <select
                        id="stat-select"
                        value={selectedStat}
                        onChange={(e) => setSelectedStat(e.target.value)}
                        className="block px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-gray-100"
                    >
                        {statOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select> */}
                    <Dropdown
                        id="stat-select"
                        title="Select Stat"
                        items={statOptions}
                        activeItem={selectedStat}
                        onSelect={setSelectedStat}
                        size="md"
                        variant="default"
                        className="w-full md:w-auto"
                    />
                </div>
            </div>

            {chartData && (
                <PlayerStatsChart 
                    data={chartData} 
                    options={chartOptions}
                    chartLabel={statOptions.find(opt => opt.value === selectedStat)?.label || selectedStat}
                />
            )}
        </div>
    );
};

export default ChartContainer;

